import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, ArrowRight, Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function CheckEmailPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);

  const handleResend = async () => {
    setResending(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success("Verification email sent! Check your inbox.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send email. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const handleCheckAndContinue = async () => {
    setChecking(true);
    try {
      await refreshUser();
      const res = await api.get("/auth/me");
      if (res.data.email_verified) {
        navigate("/onboarding");
      } else {
        toast.error("Email not yet verified. Please check your inbox and click the verification link.");
      }
    } catch {
      toast.error("Could not check verification status.");
    } finally {
      setChecking(false);
    }
  };

  if (user?.email_verified) {
    navigate("/onboarding");
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-card border border-border rounded-2xl p-8 text-center shadow-xl">
          <div className="relative mx-auto mb-6 w-20 h-20">
            <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" style={{ animationDuration: "2s" }} />
            <div className="relative w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
              <Mail className="w-10 h-10 text-primary" />
            </div>
          </div>

          <h1 className="text-2xl font-bold mb-2">Verify your email</h1>
          
          <p className="text-muted-foreground mb-2">
            We sent a verification link to:
          </p>
          
          <p className="font-medium text-foreground mb-6 bg-secondary/50 py-2 px-4 rounded-lg inline-block">
            {user?.email || "your email"}
          </p>

          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm text-amber-700 dark:text-amber-400">
              <strong>Required:</strong> You must verify your email before you can use Smart-QR. 
              Check your spam folder if you don&apos;t see it.
            </p>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={handleCheckAndContinue} 
              className="w-full gap-2"
              disabled={checking}
              data-testid="continue-to-setup-btn"
            >
              {checking ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  I&apos;ve verified &mdash; continue
                  <ArrowRight size={16} />
                </>
              )}
            </Button>
            
            <Button 
              variant="outline" 
              onClick={handleResend}
              disabled={resending}
              className="w-full gap-2"
              data-testid="resend-email-btn"
            >
              {resending ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <RefreshCw size={16} />
                  Resend verification email
                </>
              )}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground mt-6">
            Click the link in your email, then press the button above to continue.
          </p>
        </div>
      </div>
    </div>
  );
}
